import { OrderProductOutput } from "@/interfaces";
import { Package, Box, Plus } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { numberToMoney } from "@/util/moneyConversion";

interface OrderProductListProps {
  products: OrderProductOutput[];
}

export function OrderProductList({
  products,
}: Readonly<OrderProductListProps>) {
  return (
    <div className="w-full">
      <Separator className="my-4" />
      <div className="flex items-center gap-2 mb-4">
        <Box className="w-4 h-4 text-primary" />
        <h3 className="text-lg font-semibold">Produtos</h3>
      </div>

      <div className="space-y-2">
        {products.map((item) => {
          const unitPrice = item.unitPrice || 0;
          const unitCost = item.unitCost || 0;
          const margin = unitPrice - unitCost;
          const marginPercentage =
            unitPrice > 0 ? (margin / unitPrice) * 100 : 0;

          return (
            <div
              key={item.productId}
              className="flex items-start gap-2 p-2 border rounded-lg bg-gray-50/30"
            >
              <Package className="h-4 w-4 shrink-0 text-primary mt-1" />
              <div className="min-w-0 flex-1">
                <h4
                  className="font-medium truncate text-sm mb-1"
                  title={item.name}
                >
                  {item.name}
                </h4>
                {item.observation && (
                  <p className="text-xs text-gray-600 mb-2 italic line-clamp-2">
                    Obs: {item.observation}
                  </p>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500 block">Quantidade:</span>
                    <span className="font-medium">{item.quantity}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 block">Preço Unit:</span>
                    <span className="font-medium">
                      {numberToMoney(unitPrice)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 block">Margem:</span>
                    <div className="flex flex-col">
                      <span
                        className={`font-medium text-xs ${
                          margin >= 0 ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {marginPercentage.toFixed(1)}%
                      </span>
                      <span
                        className={`text-xs ${
                          margin >= 0 ? "text-green-500" : "text-red-500"
                        }`}
                      >
                        {numberToMoney(margin)}
                      </span>
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500 block">Subtotal:</span>
                    <span className="font-medium">
                      {numberToMoney(unitPrice * item.quantity)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 block">Margem Total:</span>
                    <span
                      className={`font-medium text-sm ${
                        margin * item.quantity >= 0
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {numberToMoney(margin * item.quantity)}
                    </span>
                  </div>
                </div>
                
                {item.additionals && item.additionals.length > 0 && (
                  <div className="mt-3 pl-6 border-l-2 border-orange-200">
                    <div className="flex items-center gap-1 mb-3">
                      <Plus className="h-4 w-4 text-orange-500" />
                      <span className="text-sm font-semibold text-gray-800">
                        Adicionais ({item.additionals.length})
                      </span>
                    </div>
                    <div className="space-y-2">
                      {item.additionals.map((additional, index) => (
                        <div
                          key={`${additional.itemId}-${index}`}
                          className="bg-orange-50 border border-orange-100 p-3 rounded-lg"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold text-gray-900 text-sm">
                                  {additional.label}
                                </span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                  Qtd: {additional.quantity}
                                </span>
                              </div>
                              <div className="flex items-center gap-4 text-xs text-gray-500">
                                <span>Preço unitário: {numberToMoney(additional.unitPrice)}</span>
                                {additional.unitCost > 0 && (
                                  <span>Custo: {numberToMoney(additional.unitCost)}</span>
                                )}
                                {additional.consumptionFactor && additional.consumptionFactor !== 1 && (
                                  <span>Fator: {additional.consumptionFactor}x</span>
                                )}
                              </div>
                            </div>
                            <div className="text-right ml-3">
                              <div className="font-bold text-orange-600 text-sm">
                                {numberToMoney(additional.unitPrice * additional.quantity)}
                              </div>
                              <div className="text-xs text-gray-500">
                                Total adicional
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
